import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { StatCard } from '@/components/shared/stat-card';
import {
  JobDeadLetterDetailDialog,
  JobRunDetailDialog,
  ScheduledJobDetailDialog,
  type DeadLetterRow,
  type JobRunRow,
  type ScheduledJobRow,
} from '@/components/platform-operations-detail-dialogs';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, type OffsetPage } from '@/lib/api';
import { formatDateTime } from '@/utils/format';

interface OperationsOverview {
  scheduler: { enabled: boolean };
  jobs: ScheduledJobRow[];
  deadLettersUnresolved: number;
  services: {
    uptimeSeconds: number;
    database: 'ok' | 'unavailable';
    email: {
      primaryProvider: string;
      fallbackProvider: string;
      providers: Record<string, { isConfigured: boolean }>;
    };
    sms: {
      transportMode: string;
      httpConfigured: boolean;
      smppConfigured: boolean;
      smppConnected: boolean;
    };
  };
}

type OperationsTab = 'jobs' | 'history' | 'deadLetters';

const outcomeBadgeVariant = (outcome: string): BadgeVariant => {
  if (outcome === 'succeeded') return 'success';
  if (outcome === 'partial') return 'warning';
  if (outcome === 'failed' || outcome === 'dead_lettered') return 'danger';
  if (outcome === 'running') return 'info';
  return 'neutral';
};

const formatInterval = (everyMs: number, t: (key: string, options?: Record<string, unknown>) => string): string => {
  const hourMs = 60 * 60 * 1000;
  const minuteMs = 60 * 1000;
  if (everyMs % hourMs === 0) {
    return t('operations.intervals.hours', { count: everyMs / hourMs });
  }
  if (everyMs % minuteMs === 0) {
    return t('operations.intervals.minutes', { count: everyMs / minuteMs });
  }
  return t('operations.intervals.seconds', { count: Math.round(everyMs / 1000) });
};

const formatUptime = (seconds: number, t: (key: string, options?: Record<string, unknown>) => string): string => {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) {
    return t('operations.uptime.daysHours', { days, hours });
  }
  if (hours > 0) {
    return t('operations.uptime.hoursMinutes', { hours, minutes });
  }
  return t('operations.uptime.minutes', { count: minutes });
};

const tabFromSearch = (value: string | null): OperationsTab => {
  if (value === 'history' || value === 'deadLetters') return value;
  return 'jobs';
};

export const OperationsPage = () => {
  const { t } = useTranslation('admin');
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<OperationsTab>(() =>
    tabFromSearch(searchParams.get('tab')),
  );
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(25);
  const [deadLetterPage, setDeadLetterPage] = useState(1);
  const [deadLetterPageSize, setDeadLetterPageSize] = useState(25);
  const [selectedJob, setSelectedJob] = useState<ScheduledJobRow | null>(null);
  const [selectedRun, setSelectedRun] = useState<JobRunRow | null>(null);
  const [selectedDeadLetter, setSelectedDeadLetter] = useState<DeadLetterRow | null>(null);

  const overview = useQuery({
    queryKey: ['platform-operations'],
    queryFn: () => api.get<OperationsOverview>('/platform/operations'),
  });

  const history = useQuery({
    queryKey: ['platform-job-runs', historyPage, historyPageSize],
    queryFn: () =>
      api.get<{ data: JobRunRow[]; page: OffsetPage }>('/platform/jobs/runs', {
        query: { page: historyPage, pageSize: historyPageSize },
      }),
    enabled: activeTab === 'history',
  });

  const deadLetters = useQuery({
    queryKey: ['platform-job-dead-letters', deadLetterPage, deadLetterPageSize],
    queryFn: () =>
      api.get<{ data: DeadLetterRow[]; page: OffsetPage }>('/platform/jobs/dead-letters', {
        query: { page: deadLetterPage, pageSize: deadLetterPageSize, unresolvedOnly: 'true' },
      }),
    enabled: activeTab === 'deadLetters',
  });

  const jobLabel = (name: string) =>
    t(`operations.jobNames.${name}`, { defaultValue: name });

  const jobColumns: DataTableColumn<ScheduledJobRow>[] = [
    {
      id: 'name',
      header: t('operations.columns.job'),
      cell: (row) => (
        <div>
          <p className="font-medium">{jobLabel(row.name)}</p>
          <p className="text-caption text-fg-secondary">{row.name}</p>
        </div>
      ),
    },
    {
      id: 'schedule',
      header: t('operations.columns.schedule'),
      cell: (row) => formatInterval(row.everyMs, t),
    },
    {
      id: 'scope',
      header: t('operations.columns.scope'),
      cell: (row) => t(`operations.scopes.${row.scope}`),
    },
    {
      id: 'lastRun',
      header: t('operations.columns.lastRun'),
      cell: (row) => (row.lastRun ? formatDateTime(row.lastRun.startedAt) : '—'),
    },
    {
      id: 'outcome',
      header: t('operations.columns.outcome'),
      cell: (row) =>
        row.lastRun ? (
          <Badge variant={outcomeBadgeVariant(row.lastRun.outcome)}>
            {t(`operations.outcomes.${row.lastRun.outcome as 'succeeded'}`)}
          </Badge>
        ) : (
          '—'
        ),
    },
    {
      id: 'processed',
      header: t('operations.columns.processed'),
      align: 'right',
      cell: (row) => (row.lastRun ? String(row.lastRun.itemsProcessed) : '—'),
      hideBelowMd: true,
    },
    {
      id: 'failed',
      header: t('operations.columns.failed'),
      align: 'right',
      cell: (row) => (row.lastRun ? String(row.lastRun.itemsFailed) : '—'),
      hideBelowMd: true,
    },
  ];

  const historyColumns: DataTableColumn<JobRunRow>[] = [
    {
      id: 'startedAt',
      header: t('operations.columns.startedAt'),
      cell: (row) => formatDateTime(row.startedAt),
    },
    {
      id: 'jobName',
      header: t('operations.columns.job'),
      cell: (row) => jobLabel(row.jobName),
    },
    {
      id: 'outcome',
      header: t('operations.columns.outcome'),
      cell: (row) => (
        <Badge variant={outcomeBadgeVariant(row.outcome)}>
          {t(`operations.outcomes.${row.outcome as 'succeeded'}`)}
        </Badge>
      ),
    },
    {
      id: 'processed',
      header: t('operations.columns.processed'),
      align: 'right',
      cell: (row) => String(row.itemsProcessed),
    },
    {
      id: 'failed',
      header: t('operations.columns.failed'),
      align: 'right',
      cell: (row) => String(row.itemsFailed),
    },
    {
      id: 'attempt',
      header: t('operations.columns.attempt'),
      align: 'right',
      cell: (row) => String(row.attempt),
      hideBelowMd: true,
    },
  ];

  const deadLetterColumns: DataTableColumn<DeadLetterRow>[] = [
    {
      id: 'createdAt',
      header: t('operations.columns.startedAt'),
      cell: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'jobName',
      header: t('operations.columns.job'),
      cell: (row) => jobLabel(row.jobName),
    },
    {
      id: 'church',
      header: t('operations.columns.church'),
      cell: (row) => row.churchId ?? '—',
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('operations.columns.status'),
      cell: (row) => (
        <Badge variant={row.resolvedAt ? 'neutral' : 'danger'}>
          {row.resolvedAt ? t('operations.deadLetters.resolved') : t('operations.deadLetters.open')}
        </Badge>
      ),
    },
    {
      id: 'error',
      header: t('operations.columns.error'),
      cell: (row) => <span className="line-clamp-2 text-body-sm">{row.error}</span>,
    },
  ];

  const services = overview.data?.services;
  const historyPageMeta = history.data?.page;
  const deadLetterPageMeta = deadLetters.data?.page;

  return (
    <div className="space-y-8">
      <PageHeader title={t('operations.title')} description={t('operations.description')} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t('operations.stats.scheduler')}
          value={
            overview.data?.scheduler.enabled
              ? t('operations.stats.schedulerOn')
              : t('operations.stats.schedulerOff')
          }
          loading={overview.isLoading}
        />
        <StatCard
          label={t('operations.stats.database')}
          value={
            services?.database === 'ok'
              ? t('operations.stats.databaseOk')
              : t('operations.stats.databaseUnavailable')
          }
          loading={overview.isLoading}
        />
        <StatCard
          label={t('operations.stats.deadLetters')}
          value={String(overview.data?.deadLettersUnresolved ?? 0)}
          loading={overview.isLoading}
        />
        <StatCard
          label={t('operations.stats.uptime')}
          value={services ? formatUptime(services.uptimeSeconds, t) : '—'}
          loading={overview.isLoading}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('operations.services.emailTitle')}</CardTitle>
            <CardDescription>{t('operations.services.emailDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-body-sm">{t('operations.services.primaryProvider')}</span>
              <span className="text-body-sm font-medium capitalize">
                {services?.email.primaryProvider ?? '—'}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-body-sm">{t('operations.services.fallbackProvider')}</span>
              <span className="text-body-sm font-medium capitalize">
                {services?.email.fallbackProvider === 'none'
                  ? t('operations.services.noFallback')
                  : services?.email.fallbackProvider}
              </span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {(['alibaba', 'postmark'] as const).map((provider) => (
                <div key={provider} className="flex items-center justify-between gap-2">
                  <span className="text-body-sm">{t(`email.providers.${provider}`)}</span>
                  <Badge
                    variant={
                      services?.email.providers[provider]?.isConfigured ? 'success' : 'warning'
                    }
                  >
                    {services?.email.providers[provider]?.isConfigured
                      ? t('email.configured')
                      : t('email.notConfigured')}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('operations.services.smsTitle')}</CardTitle>
            <CardDescription>{t('operations.services.smsDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-body-sm">{t('operations.services.sendMethod')}</span>
              <span className="text-body-sm font-medium">
                {services
                  ? t(`sms.modes.${services.sms.transportMode as 'adaptive'}`)
                  : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-body-sm">{t('sms.httpLabel')}</span>
              <Badge variant={services?.sms.httpConfigured ? 'success' : 'warning'}>
                {services?.sms.httpConfigured ? t('sms.configured') : t('sms.notConfigured')}
              </Badge>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-body-sm">{t('sms.smppLabel')}</span>
              <Badge variant={services?.sms.smppConfigured ? 'success' : 'warning'}>
                {services?.sms.smppConfigured ? t('sms.configured') : t('sms.notConfigured')}
              </Badge>
            </div>
            {services?.sms.smppConfigured ? (
              <div className="flex items-center justify-between gap-2">
                <span className="text-body-sm">{t('sms.smppConnection')}</span>
                <Badge variant={services.sms.smppConnected ? 'success' : 'neutral'}>
                  {services.sms.smppConnected ? t('sms.smppConnected') : t('sms.smppIdle')}
                </Badge>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          const next = value as OperationsTab;
          setActiveTab(next);
          const nextParams = new URLSearchParams(searchParams);
          if (next === 'jobs') nextParams.delete('tab');
          else nextParams.set('tab', next);
          setSearchParams(nextParams, { replace: true });
        }}
      >
        <TabsList>
          <TabsTrigger value="jobs">{t('operations.tabs.jobs')}</TabsTrigger>
          <TabsTrigger value="history">{t('operations.tabs.history')}</TabsTrigger>
          <TabsTrigger value="deadLetters">{t('operations.tabs.deadLetters')}</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="space-y-3">
          <p className="text-body-sm text-fg-secondary">{t('operations.jobsDescription')}</p>
          <DataTable
            rows={overview.data?.jobs ?? []}
            columns={jobColumns}
            rowKey={(row) => row.name}
            loading={overview.isLoading}
            onRowClick={setSelectedJob}
            empty={
              <p className="text-body-sm text-fg-secondary">{t('operations.jobsEmpty')}</p>
            }
          />
        </TabsContent>

        <TabsContent value="history" className="space-y-3">
          <p className="text-body-sm text-fg-secondary">{t('operations.historyDescription')}</p>
          <DataTable
            rows={history.data?.data ?? []}
            columns={historyColumns}
            rowKey={(row) => row.id}
            loading={history.isLoading}
            onRowClick={setSelectedRun}
            empty={
              <p className="text-body-sm text-fg-secondary">{t('operations.historyEmpty')}</p>
            }
          />
          {historyPageMeta && historyPageMeta.totalPages > 1 ? (
            <Pagination
              page={historyPageMeta.page}
              pageSize={historyPageMeta.pageSize}
              totalItems={historyPageMeta.totalItems}
              totalPages={historyPageMeta.totalPages}
              onPageChange={setHistoryPage}
              onPageSizeChange={(next) => {
                setHistoryPageSize(next);
                setHistoryPage(1);
              }}
              labels={{
                showing: ({ from, to, total }) => t('pagination.showing', { from, to, total }),
                perPage: t('pagination.perPage', { defaultValue: 'Per page' }),
                first: t('pagination.first', { defaultValue: 'First' }),
                previous: t('pagination.previous'),
                next: t('pagination.next'),
                last: t('pagination.last', { defaultValue: 'Last' }),
              }}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="deadLetters" className="space-y-3">
          <p className="text-body-sm text-fg-secondary">{t('operations.deadLetters.description')}</p>
          <DataTable
            rows={deadLetters.data?.data ?? []}
            columns={deadLetterColumns}
            rowKey={(row) => row.id}
            loading={deadLetters.isLoading}
            onRowClick={setSelectedDeadLetter}
            empty={
              <p className="text-body-sm text-fg-secondary">{t('operations.deadLetters.empty')}</p>
            }
          />
          {deadLetterPageMeta && deadLetterPageMeta.totalPages > 1 ? (
            <Pagination
              page={deadLetterPageMeta.page}
              pageSize={deadLetterPageMeta.pageSize}
              totalItems={deadLetterPageMeta.totalItems}
              totalPages={deadLetterPageMeta.totalPages}
              onPageChange={setDeadLetterPage}
              onPageSizeChange={(next) => {
                setDeadLetterPageSize(next);
                setDeadLetterPage(1);
              }}
              labels={{
                showing: ({ from, to, total }) => t('pagination.showing', { from, to, total }),
                perPage: t('pagination.perPage', { defaultValue: 'Per page' }),
                first: t('pagination.first', { defaultValue: 'First' }),
                previous: t('pagination.previous'),
                next: t('pagination.next'),
                last: t('pagination.last', { defaultValue: 'Last' }),
              }}
            />
          ) : null}
        </TabsContent>
      </Tabs>

      <ScheduledJobDetailDialog
        key={selectedJob?.name ?? 'scheduled-job'}
        job={selectedJob}
        schedulerEnabled={overview.data?.scheduler.enabled ?? false}
        open={selectedJob != null}
        onOpenChange={(open) => {
          if (!open) setSelectedJob(null);
        }}
      />
      <JobRunDetailDialog
        key={selectedRun?.id ?? 'job-run'}
        run={selectedRun}
        open={selectedRun != null}
        onOpenChange={(open) => {
          if (!open) setSelectedRun(null);
        }}
      />
      <JobDeadLetterDetailDialog
        key={selectedDeadLetter?.id ?? 'dead-letter'}
        deadLetter={selectedDeadLetter}
        open={selectedDeadLetter != null}
        onOpenChange={(open) => {
          if (!open) setSelectedDeadLetter(null);
        }}
      />
    </div>
  );
};
